import { useData } from "vike-react/useData";
import type { Data } from "./+data";
import type { Review } from "@/types/review";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReviewCard from "@/components/ReviewCard";

// O2 (SEO FLOW): server-rendered review archive. Everything here must reach the
// server HTML — plain <a> pagination, no client fetching, no portals. ReviewCard
// is SSR-safe (deterministic clamp, no DOM measuring) and all its interaction
// handlers are optional, so it renders read-only here.
export default function Page() {
  const { store, reviews, pagination, page, error } = useData<Data>();

  if (!store) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16 text-center font-arabic">
          <p>{error || "المتجر غير موجود"}</p>
        </main>
        <Footer />
      </div>
    );
  }

  const slug = store.slug || store._id;
  const storeUrl = `/store/${slug}`;
  const baseUrl = `${storeUrl}/reviews`;
  const totalItems = pagination?.totalItems ?? store.totalReviews;
  const totalPages = pagination?.totalPages ?? 1;
  const prevHref = page - 1 <= 1 ? baseUrl : `${baseUrl}?page=${page - 1}`;
  const nextHref = `${baseUrl}?page=${page + 1}`;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-3xl font-arabic">
        <nav className="text-sm text-muted-foreground mb-4">
          <a href="/" className="hover:underline">
            الرئيسية
          </a>
          {" · "}
          <a href={storeUrl} className="hover:underline">
            {store.name}
          </a>
          {" · "}
          <span>التقييمات</span>
        </nav>

        <h1 className="text-2xl font-bold mb-1">تقييمات {store.name}</h1>
        {totalItems > 0 && (
          <p className="text-muted-foreground mb-6">
            {totalItems} تقييم من المتسوقين
            {totalPages > 1 && ` (صفحة ${page} من ${totalPages})`}
          </p>
        )}

        {/* Write-review CTA. A plain <a>, like everything else on this page: the archive is
            server-rendered and deliberately non-interactive, so the intent is handed to the
            store page via ?writeReview=1, which ReviewsSection consumes through its normal
            auth + draft gates. Placed above the list so it is reachable without scrolling
            past a full page of cards. */}
        <p className="mb-6">
          <a
            href={`${storeUrl}?writeReview=1`}
            className="text-primary hover:underline font-semibold"
          >
            أضف تقييمك
          </a>
        </p>

        {reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map((review) => (
              <ReviewCard
                key={review._id}
                review={review as unknown as Review}
                storeName={store.name}
                storeLogoUrl={store.logoUrl}
              />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground py-12 text-center">
            لا توجد تقييمات في هذه الصفحة.
          </p>
        )}

        {/* Crawlable pagination — plain anchors, never client-side handlers */}
        {(pagination?.hasPrevPage || pagination?.hasNextPage) && (
          <nav className="flex items-center justify-between mt-8 text-sm">
            {pagination?.hasPrevPage ? (
              <a href={prevHref} className="hover:underline text-primary">
                → الصفحة السابقة
              </a>
            ) : (
              <span />
            )}
            {pagination?.hasNextPage ? (
              <a href={nextHref} className="hover:underline text-primary">
                الصفحة التالية ←
              </a>
            ) : (
              <span />
            )}
          </nav>
        )}

        <div className="mt-10 text-center">
          <a href={storeUrl} className="text-primary hover:underline">
            العودة إلى صفحة {store.name}
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
